import {
  AlertTriangle,
  CheckCircle2,
  History,
  LogOut,
  Pause,
  Play,
  Plus,
  Search,
  Settings,
  Terminal,
  Trash2,
} from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { hasActiveMaintenance, MaintenanceIndicator } from '@/components/MaintenanceWarning/MaintenanceWarning';
import { StartupHistoryModal } from '@/components/StartupTimeline/StartupHistoryModal';
import { computeProgress } from '@/components/StartupTimeline/StartupProgressBar';
import { Button } from '@/components/ui/button';
import { MultiSelect, type MultiSelectOption } from '@/components/ui/multi-select';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { WorkspaceImage, WorkspaceProgressStep, WorkspaceVersion } from '@/lib/types/rome';
import { WorkspaceState } from '@/lib/types/rome';
import { cn } from '@/lib/utils/cn';
import { formatShortDate } from '@/lib/utils/date';
import { orgHref } from '@/lib/utils/orgRoutes';

const ALL_STATES = Object.values(WorkspaceState);

const STATE_LABELS: Record<WorkspaceState, string> = {
  [WorkspaceState.STARTING]: 'Starting',
  [WorkspaceState.RUNNING]: 'Running',
  [WorkspaceState.STOPPING]: 'Pausing',
  [WorkspaceState.STOPPED]: 'Paused',
  [WorkspaceState.ERRORED]: 'Errored',
  [WorkspaceState.TERMINATING]: 'Deleting',
  [WorkspaceState.TERMINATED]: 'Deleted',
};

export interface Workspace {
  id: string;
  name: string;
  hostname: string;
  createdAt: number;
  /** Timestamp when current session started running */
  runningAt?: number;
  /** Timestamp when workspace was stopped */
  stoppedAt?: number;
  /** Timestamp when workspace entered errored state */
  erroredAt?: number;
  /** antioch-sim version */
  version?: string;
  /** Workspace image name; undefined means the central antioch-sim image */
  image?: string;
  /** Human-readable reason for last startup failure */
  failureReason?: string | null;
  /** Unix timestamp (seconds) when the workspace will auto-pause for scheduled node maintenance; 0/undefined if none */
  scheduledShutdownAt?: number;
  state: WorkspaceState;
  ipAddress?: string;
}

/** The central image every workspace defaults to */
const DEFAULT_IMAGE = 'antioch-sim';

export interface WorkspaceTableProps {
  workspaces: Workspace[];
  onViewWorkspace?: (workspace: Workspace) => void;
  onStartWorkspace?: (workspaceName: string, version: string, image?: string) => void;
  onRestartWorkspace?: (workspace: Workspace, version?: string, image?: string) => void;
  onStopWorkspace?: (workspace: Workspace) => void;
  onTerminateWorkspace?: (workspace: Workspace) => void;
  onDisconnect?: () => void;
  title?: string;
  className?: string;
  showStartButton?: boolean;
  isStarting?: boolean;
  isLoading?: boolean;
  availableVersions?: WorkspaceVersion[];
  /** Full image catalog (central + org images); drives the image picker and per-image version lists */
  availableImages?: WorkspaceImage[];
  /** Set when the org's custom-image listing failed; central versions still work */
  orgImagesError?: string | null;
  versionsLoading?: boolean;
  readOnlyWorkspaceIds?: Set<string>;
  connectedWorkspaceId?: string;
  restartingWorkspaceId?: string | null;
  progressMap?: Record<string, WorkspaceProgressStep[]>;
  /** Cap visible rows; overflow scrolls internally */
  maxRows?: number;
  fetchProgressForWorkspace?: (workspaceId: string) => Promise<void>;
}

export default function WorkspaceTable({
  workspaces,
  onViewWorkspace,
  onStartWorkspace,
  onRestartWorkspace,
  onStopWorkspace,
  onTerminateWorkspace,
  onDisconnect,
  title = 'Workspaces',
  className,
  showStartButton = true,
  isStarting = false,
  isLoading = false,
  availableVersions = [],
  availableImages = [],
  orgImagesError = null,
  versionsLoading = false,
  readOnlyWorkspaceIds,
  connectedWorkspaceId,
  restartingWorkspaceId: _restartingWorkspaceId,
  progressMap = {},
  maxRows,
  fetchProgressForWorkspace,
}: WorkspaceTableProps) {
  const [showStartDialog, setShowStartDialog] = React.useState(false);
  const [progressModalWorkspaceId, setProgressModalWorkspaceId] = React.useState<string | null>(null);
  const [workspaceName, setWorkspaceName] = React.useState('');
  const [selectedVersion, setSelectedVersion] = React.useState('');
  const [selectedImage, setSelectedImage] = React.useState(DEFAULT_IMAGE);
  const [nameError, setNameError] = React.useState<string | null>(null);
  const [, setTick] = React.useState(0);

  // Restart dialog state
  const [showRestartDialog, setShowRestartDialog] = React.useState(false);
  const [workspaceToRestart, setWorkspaceToRestart] = React.useState<Workspace | null>(null);
  const [restartVersion, setRestartVersion] = React.useState('');
  const [restartImage, setRestartImage] = React.useState(DEFAULT_IMAGE);

  const startDialogRef = React.useRef<HTMLDivElement>(null);
  const nameInputRef = React.useRef<HTMLInputElement>(null);

  // Search and filter state
  const [searchQuery, setSearchQuery] = React.useState('');
  const [selectedStates, setSelectedStates] = React.useState<Set<WorkspaceState>>(
    new Set([
      WorkspaceState.STARTING,
      WorkspaceState.RUNNING,
      WorkspaceState.STOPPING,
      WorkspaceState.STOPPED,
      WorkspaceState.ERRORED,
    ])
  );

  // The org's preselected image: the entry the server flagged default:true
  // (per-org policy), falling back to the central image. Drives dialog
  // initialization so a policy org's picker opens on their image.
  const defaultImageName = React.useMemo(
    () => availableImages.find((img) => img.default)?.name ?? DEFAULT_IMAGE,
    [availableImages]
  );

  // Distinct image names in the catalog, the org's default first. Only built
  // (image, version) pairs exist in availableImages, so filtering versions
  // by the selected image keeps the two dropdowns mutually consistent. An
  // org policy may exclude the central image entirely, so the list derives
  // purely from the (already policy-filtered) catalog; the central name is
  // only a fallback when no catalog loaded.
  const imageNames = React.useMemo(() => {
    const names: string[] = [];
    for (const img of availableImages) {
      if (!names.includes(img.name)) names.push(img.name);
    }
    if (names.length === 0) return [DEFAULT_IMAGE];
    return [...names.filter((n) => n === defaultImageName), ...names.filter((n) => n !== defaultImageName)];
  }, [availableImages, defaultImageName]);

  const hasImageChoice = imageNames.length > 1;

  // Show the picker when the org has more than one visible image, or when
  // the custom-image listing failed (render the picker as unavailable
  // instead of hiding it silently).
  const showImagePicker = hasImageChoice || Boolean(orgImagesError);

  const versionsForImage = React.useCallback(
    (imageName: string): WorkspaceVersion[] => {
      // Fall back to the plain version list when no image catalog was passed
      if (availableImages.length === 0) return availableVersions;
      return availableImages
        .filter((img) => img.name === imageName)
        .map((img) => ({
          version: img.version,
          image_tag: img.image_tag,
          image: `${img.repository}:${img.image_tag}`,
          released_at: img.released_at,
        }));
    },
    [availableImages, availableVersions]
  );

  // The friendly label for an image name. Rome resolves display_name only for
  // the owning org (or a staff reveal); otherwise it is absent and we show the
  // opaque name. The image is still selected/started by its opaque `name`.
  const imageLabel = React.useCallback(
    (imageName: string): string => availableImages.find((img) => img.name === imageName)?.display_name ?? imageName,
    [availableImages]
  );

  // Workspace name validation regex (matches backend)
  const WORKSPACE_NAME_REGEX = /^[a-zA-Z0-9][a-zA-Z0-9 _-]{0,63}$/;

  // Count workspaces by state
  const countsByState = React.useMemo(() => {
    const counts: Record<WorkspaceState, number> = {
      [WorkspaceState.STARTING]: 0,
      [WorkspaceState.RUNNING]: 0,
      [WorkspaceState.STOPPING]: 0,
      [WorkspaceState.STOPPED]: 0,
      [WorkspaceState.ERRORED]: 0,
      [WorkspaceState.TERMINATING]: 0,
      [WorkspaceState.TERMINATED]: 0,
    };
    for (const w of workspaces) {
      if (w.state in counts) counts[w.state]++;
    }
    return counts;
  }, [workspaces]);

  // State filter options
  const stateOptions: MultiSelectOption<WorkspaceState>[] = React.useMemo(() => {
    return ALL_STATES.map((state) => ({
      value: state,
      label: STATE_LABELS[state],
      count: countsByState[state],
    }));
  }, [countsByState]);

  // Filter workspaces by search query and selected states
  const filteredWorkspaces = React.useMemo(() => {
    return workspaces.filter((w) => {
      if (!selectedStates.has(w.state)) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const fields = [w.name, w.id, w.hostname];
        return fields.some((f) => f?.toLowerCase().includes(query));
      }
      return true;
    });
  }, [workspaces, selectedStates, searchQuery]);

  const handleViewWorkspace = (workspace: Workspace) => {
    if (onViewWorkspace && workspace.state === WorkspaceState.RUNNING) {
      onViewWorkspace(workspace);
    }
  };

  const handleStartClick = () => {
    setWorkspaceName('');
    setSelectedImage(defaultImageName);
    const versions = versionsForImage(defaultImageName);
    setSelectedVersion(versions.length > 0 ? versions[0].version : '');
    setNameError(null);
    setShowStartDialog(true);
  };

  // Switching image re-pins the version to that image's latest when the
  // current selection isn't built for it - only built pairs are selectable.
  const handleStartImageChange = React.useCallback(
    (imageName: string) => {
      setSelectedImage(imageName);
      const versions = versionsForImage(imageName);
      if (!versions.some((v) => v.version === selectedVersion)) {
        setSelectedVersion(versions.length > 0 ? versions[0].version : '');
      }
    },
    [versionsForImage, selectedVersion]
  );

  const validateName = (name: string): boolean => {
    if (!name.trim()) {
      setNameError('Workspace name is required');
      return false;
    }
    if (!WORKSPACE_NAME_REGEX.test(name)) {
      setNameError(
        'Name must start with a letter or number and contain only letters, numbers, spaces, hyphens, or underscores'
      );
      return false;
    }
    setNameError(null);
    return true;
  };

  // Track whether we've submitted and are waiting for the operation to complete
  const [hasSubmitted, setHasSubmitted] = React.useState(false);

  const handleStartConfirm = () => {
    if (!validateName(workspaceName)) {
      return;
    }

    setHasSubmitted(true);
    if (onStartWorkspace) {
      onStartWorkspace(workspaceName.trim(), selectedVersion, availableImages.length > 0 ? selectedImage : undefined);
    }
  };

  // Close dialog when starting completes (only after user has submitted)
  React.useEffect(() => {
    if (hasSubmitted && !isStarting) {
      setShowStartDialog(false);
      setWorkspaceName('');
      setNameError(null);
      setHasSubmitted(false);
    }
  }, [hasSubmitted, isStarting]);

  const handleStartCancel = React.useCallback(() => {
    setShowStartDialog(false);
    setWorkspaceName('');
    setSelectedVersion('');
    setSelectedImage(DEFAULT_IMAGE);
    setNameError(null);
  }, []);

  // Restart dialog handlers
  const handleRestartClick = React.useCallback(
    (workspace: Workspace) => {
      const image = workspace.image || DEFAULT_IMAGE;
      const versions = versionsForImage(image);
      setWorkspaceToRestart(workspace);
      setRestartImage(image);
      setRestartVersion(workspace.version || (versions.length > 0 ? versions[0].version : ''));
      setShowRestartDialog(true);
    },
    [versionsForImage]
  );

  const handleRestartImageChange = React.useCallback(
    (imageName: string) => {
      setRestartImage(imageName);
      const versions = versionsForImage(imageName);
      const pinned = workspaceToRestart?.image || DEFAULT_IMAGE;
      const pinnedVersion = workspaceToRestart?.version || '';
      // Back on the workspace's own image, its pinned version is restartable
      // even when hidden from the catalog; any other image needs a built pair.
      if (imageName === pinned && pinnedVersion) {
        setRestartVersion(pinnedVersion);
      } else if (!versions.some((v) => v.version === restartVersion)) {
        setRestartVersion(versions.length > 0 ? versions[0].version : '');
      }
    },
    [versionsForImage, workspaceToRestart, restartVersion]
  );

  const handleRestartConfirm = React.useCallback(() => {
    if (workspaceToRestart && onRestartWorkspace) {
      onRestartWorkspace(
        workspaceToRestart,
        restartVersion || undefined,
        availableImages.length > 0 ? restartImage : undefined
      );
    }
    setShowRestartDialog(false);
    setWorkspaceToRestart(null);
    setRestartVersion('');
    setRestartImage(DEFAULT_IMAGE);
  }, [workspaceToRestart, restartVersion, restartImage, availableImages, onRestartWorkspace]);

  const handleRestartCancel = React.useCallback(() => {
    setShowRestartDialog(false);
    setWorkspaceToRestart(null);
    setRestartVersion('');
    setRestartImage(DEFAULT_IMAGE);
  }, []);

  const formatDuration = (timestamp: number): string => {
    const now = Math.floor(Date.now() / 1000);
    const seconds = now - timestamp;

    if (seconds < 60) return `${seconds}s`;

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m`;

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (hours < 24) return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;

    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
  };

  const formatTimestamp = (timestamp: number): string => {
    const date = new Date(timestamp * 1000);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    if (isToday) {
      return `Today at ${date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
    } else {
      return (
        date.toLocaleDateString([], { month: 'short', day: 'numeric' }) +
        ` at ${date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
      );
    }
  };

  // Shared badge base classes
  const badgeBase = 'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 font-medium text-xs';

  // Get state badge for workspace
  const getStateBadge = (state: WorkspaceState, startingPercent?: number) => {
    switch (state) {
      case WorkspaceState.RUNNING:
        return (
          <span className={cn(badgeBase, 'bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-400')}>
            <CheckCircle2 className="h-3.5 w-3.5" />
            Running
          </span>
        );
      case WorkspaceState.STARTING:
        return (
          <span
            className={cn(
              badgeBase,
              'relative overflow-hidden bg-blue-50 text-blue-800 dark:bg-blue-500/10 dark:text-blue-400'
            )}
          >
            {/* Progress fill background */}
            {startingPercent != null && startingPercent > 0 ? (
              <span
                className="absolute inset-y-0 left-0 bg-blue-200/80 transition-[width] duration-[1500ms] ease-out dark:bg-blue-500/25"
                style={{ width: `${Math.min(startingPercent, 100)}%` }}
              />
            ) : (
              <span
                className="absolute inset-0"
                style={{
                  backgroundImage:
                    'linear-gradient(90deg, transparent 0%, rgba(59,130,246,0.15) 50%, transparent 100%)',
                  backgroundSize: '200% 100%',
                  animation: 'progress-shimmer 2s ease-in-out infinite',
                }}
              />
            )}
            <Spinner size="h-3.5 w-3.5" className="relative" />
            <span className="relative">Starting</span>
          </span>
        );
      case WorkspaceState.STOPPING:
        return (
          <span className={cn(badgeBase, 'bg-orange-100 text-orange-800 dark:bg-orange-500/20 dark:text-orange-400')}>
            <Spinner size="h-3.5 w-3.5" />
            Pausing
          </span>
        );
      case WorkspaceState.STOPPED:
        return (
          <span className={cn(badgeBase, 'bg-yellow-100 text-yellow-800 dark:bg-yellow-500/20 dark:text-yellow-400')}>
            <Pause className="h-3.5 w-3.5" />
            Paused
          </span>
        );
      case WorkspaceState.ERRORED:
        return (
          <span className={cn(badgeBase, 'bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-400')}>
            <AlertTriangle className="h-3.5 w-3.5" />
            Errored
          </span>
        );
      case WorkspaceState.TERMINATING:
        return (
          <span className={cn(badgeBase, 'bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-400')}>
            <Spinner size="h-3.5 w-3.5" />
            Deleting
          </span>
        );
      case WorkspaceState.TERMINATED:
        return (
          <span className={cn(badgeBase, 'bg-gray-100 text-gray-500 dark:bg-white/10 dark:text-gray-400')}>
            <Trash2 className="h-3.5 w-3.5" />
            Deleted
          </span>
        );
      default:
        return (
          <span className={cn(badgeBase, 'bg-gray-100 text-gray-800 dark:bg-white/10 dark:text-gray-300')}>
            {state}
          </span>
        );
    }
  };

  // Update duration display every minute
  React.useEffect(() => {
    const interval = setInterval(() => {
      setTick((t) => t + 1);
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  // Handle escape key to close dialogs and focus input
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showStartDialog) handleStartCancel();
        if (showRestartDialog) handleRestartCancel();
      }
    };

    if (showStartDialog || showRestartDialog) {
      document.addEventListener('keydown', handleEscape);
      if (showStartDialog) {
        // Focus the name input after a brief delay to ensure it's rendered
        setTimeout(() => nameInputRef.current?.focus(), 50);
      }
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showStartDialog, showRestartDialog, handleStartCancel, handleRestartCancel]);

  return (
    <TooltipProvider>
      <div
        className={cn(
          'flex flex-col rounded-xl border border-gray-200 bg-white dark:border-white/10 dark:bg-white/5',
          className
        )}
      >
        {/* Header with Title and Controls */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-gray-100 border-b px-4 py-3 dark:border-white/5">
          <div className="flex items-center gap-2.5">
            <div className="flex size-7 items-center justify-center rounded-md bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-gray-300">
              <Terminal className="size-3.5" />
            </div>
            <h3 className="font-medium text-gray-800 text-sm dark:text-white">{title}</h3>
          </div>

          <div className="flex items-center gap-2">
            <div className="relative w-44">
              <Search className="absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-7 w-full rounded-md border border-gray-200 bg-white pr-2 pl-7 text-gray-700 text-xs placeholder:text-gray-400 focus:border-gray-300 focus:outline-none focus:ring-1 focus:ring-gray-300 dark:border-white/10 dark:bg-white/5 dark:text-gray-200 dark:focus:border-white/20 dark:placeholder:text-gray-500"
              />
            </div>
            <MultiSelect
              options={stateOptions}
              selected={selectedStates}
              onChange={setSelectedStates}
              placeholder="State"
              className="h-7"
            />
            {showStartButton && (
              <Button onClick={handleStartClick} size="sm" className="h-7 gap-1 px-2.5 text-xs">
                <Plus className="size-3" />
                New Workspace
              </Button>
            )}
          </div>
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="size-5" className="text-blue-600 dark:text-blue-400" />
          </div>
        ) : (
          // The card grows with its content rather than scrolling: maxRows sets a
          // minimum height (so a near-empty table still has presence) but the
          // table extends freely as workspaces are added -- an internal scroll
          // here is easy to miss.
          <div style={maxRows ? { minHeight: 28 + maxRows * 40 } : undefined}>
            <Table className="table-fixed">
              <TableHeader>
                <TableRow className="border-gray-100 border-b bg-gray-50 hover:bg-gray-50 dark:border-white/5 dark:bg-[#0a0f1a] dark:hover:bg-[#0a0f1a]">
                  <TableHead className="w-[28%] px-4">Name</TableHead>
                  <TableHead className="w-[22%] px-4">State</TableHead>
                  <TableHead className="w-[25%] px-4">Created</TableHead>
                  <TableHead className="w-[25%] px-4 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredWorkspaces.map((workspace) => {
                  const isConnected = connectedWorkspaceId === workspace.id;
                  const steps = progressMap[workspace.id] ?? [];

                  // Compute progress for Starting workspaces
                  const { percent: startingPercent, currentStep } =
                    workspace.state === WorkspaceState.STARTING
                      ? computeProgress(steps)
                      : { percent: 0, currentStep: null };

                  return (
                    <React.Fragment key={workspace.id}>
                      <TableRow
                        className={cn(
                          'h-10 border-gray-100 border-b dark:border-white/5',
                          isConnected && 'bg-blue-50/50 dark:bg-blue-500/5'
                        )}
                      >
                        <TableCell className="px-4">
                          <div className="flex items-baseline gap-1.5">
                            <span className="truncate font-medium text-gray-900 text-xs dark:text-gray-100">
                              {workspace.name || workspace.id}
                            </span>
                            {workspace.version && (
                              <span className="relative -top-px inline-flex items-center rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] text-gray-500 leading-none dark:bg-white/10 dark:text-gray-400">
                                v{workspace.version}
                              </span>
                            )}
                            {readOnlyWorkspaceIds?.has(workspace.id) && (
                              <span className="relative -top-px rounded bg-violet-100 px-1.5 py-0.5 font-medium text-[10px] text-violet-700 leading-none dark:bg-violet-900/30 dark:text-violet-400">
                                Local
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="px-4">
                          <div className="flex items-center gap-1.5">
                            {/* State badge with contextual tooltip */}
                            {workspace.state === WorkspaceState.STARTING ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    onClick={() => setProgressModalWorkspaceId(workspace.id)}
                                    className="cursor-pointer"
                                  >
                                    {getStateBadge(workspace.state, startingPercent)}
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="text-xs">{currentStep?.message ?? 'Starting...'}</p>
                                </TooltipContent>
                              </Tooltip>
                            ) : workspace.state === WorkspaceState.RUNNING && workspace.runningAt ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="cursor-help">{getStateBadge(workspace.state)}</span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="text-xs">Running for: {formatDuration(workspace.runningAt)}</p>
                                </TooltipContent>
                              </Tooltip>
                            ) : workspace.state === WorkspaceState.STOPPED && workspace.stoppedAt ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="cursor-help">{getStateBadge(workspace.state)}</span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="text-xs">Paused at: {formatTimestamp(workspace.stoppedAt)}</p>
                                </TooltipContent>
                              </Tooltip>
                            ) : workspace.state === WorkspaceState.ERRORED ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (!progressMap[workspace.id] && fetchProgressForWorkspace) {
                                        fetchProgressForWorkspace(workspace.id);
                                      }
                                      setProgressModalWorkspaceId(workspace.id);
                                    }}
                                    className="cursor-pointer"
                                  >
                                    {getStateBadge(workspace.state)}
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent sideOffset={6}>
                                  <p className="break-words text-xs">{workspace.failureReason || 'Startup failed'}</p>
                                </TooltipContent>
                              </Tooltip>
                            ) : (
                              getStateBadge(workspace.state)
                            )}

                            {/* Scheduled node-maintenance auto-pause indicator */}
                            {hasActiveMaintenance(workspace) && (
                              <MaintenanceIndicator scheduledShutdownAt={workspace.scheduledShutdownAt as number} />
                            )}

                            {/* History button - fetch progress on-demand when clicked */}
                            {workspace.state !== WorkspaceState.TERMINATED && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => {
                                      if (!progressMap[workspace.id] && fetchProgressForWorkspace) {
                                        fetchProgressForWorkspace(workspace.id);
                                      }
                                      setProgressModalWorkspaceId(workspace.id);
                                    }}
                                    className="h-6 w-6 shrink-0 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:text-gray-500 dark:hover:bg-white/10 dark:hover:text-gray-300"
                                  >
                                    <History className="h-3 w-3" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Startup history</TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="px-4">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-help text-gray-600 text-xs dark:text-gray-300">
                                {formatTimestamp(workspace.createdAt)}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="text-xs">Created {formatDuration(workspace.createdAt)} ago</p>
                            </TooltipContent>
                          </Tooltip>
                        </TableCell>
                        {/* Actions column */}
                        <TableCell className="px-4 text-right">
                          {(() => {
                            const isReadOnly = readOnlyWorkspaceIds?.has(workspace.id) ?? false;
                            const canConnect = workspace.state === WorkspaceState.RUNNING && !isConnected;
                            const canStart =
                              [WorkspaceState.STOPPED, WorkspaceState.ERRORED].includes(workspace.state) &&
                              !isReadOnly &&
                              !!onRestartWorkspace;
                            const canPause =
                              [WorkspaceState.STARTING, WorkspaceState.RUNNING].includes(workspace.state) &&
                              !isReadOnly &&
                              !!onStopWorkspace;
                            const canDelete =
                              [WorkspaceState.STOPPED, WorkspaceState.ERRORED].includes(workspace.state) &&
                              !isReadOnly &&
                              !!onTerminateWorkspace;
                            const disabledStyle =
                              'size-6 bg-gray-100 text-gray-300 dark:bg-white/5 dark:text-gray-600 disabled:pointer-events-auto';

                            return (
                              <div className="flex items-center justify-end gap-0.5">
                                {/* Connect / Disconnect */}
                                {isConnected ? (
                                  <Button
                                    variant="ghost"
                                    onClick={onDisconnect}
                                    className="h-6 gap-1 rounded-md bg-gray-100 px-2 text-[11px] text-gray-600 hover:bg-gray-200 hover:text-gray-800 dark:bg-white/10 dark:text-gray-300 dark:hover:bg-white/15 dark:hover:text-gray-100"
                                  >
                                    <LogOut className="size-3" />
                                    Disconnect
                                  </Button>
                                ) : (
                                  <Button
                                    variant="ghost"
                                    disabled={!canConnect}
                                    onClick={() => handleViewWorkspace(workspace)}
                                    className={cn(
                                      'h-6 gap-1 rounded-md px-2 text-[11px]',
                                      canConnect
                                        ? 'bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 dark:bg-blue-500/10 dark:text-blue-400 dark:hover:bg-blue-500/20 dark:hover:text-blue-300'
                                        : 'bg-gray-100 text-gray-300 disabled:pointer-events-auto dark:bg-white/5 dark:text-gray-600'
                                    )}
                                  >
                                    <Terminal className="size-3" />
                                    Connect
                                  </Button>
                                )}

                                {/* Start */}
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      disabled={!canStart}
                                      onClick={() => handleRestartClick(workspace)}
                                      className={
                                        canStart
                                          ? 'size-6 bg-green-50 text-green-600 hover:bg-green-100 hover:text-green-700 dark:bg-green-500/10 dark:text-green-400 dark:hover:bg-green-500/20 dark:hover:text-green-300'
                                          : disabledStyle
                                      }
                                    >
                                      <Play className="size-3" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Start</TooltipContent>
                                </Tooltip>

                                {/* Pause */}
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      disabled={!canPause}
                                      onClick={() => onStopWorkspace?.(workspace)}
                                      className={
                                        canPause
                                          ? 'size-6 bg-orange-50 text-orange-600 hover:bg-orange-100 hover:text-orange-700 dark:bg-orange-500/10 dark:text-orange-400 dark:hover:bg-orange-500/20 dark:hover:text-orange-300'
                                          : disabledStyle
                                      }
                                    >
                                      <Pause className="size-3" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Pause</TooltipContent>
                                </Tooltip>

                                {/* Delete */}
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      disabled={!canDelete}
                                      onClick={() => onTerminateWorkspace?.(workspace)}
                                      className={
                                        canDelete
                                          ? 'size-6 bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 dark:bg-red-500/10 dark:text-red-400 dark:hover:bg-red-500/20 dark:hover:text-red-300'
                                          : disabledStyle
                                      }
                                    >
                                      <Trash2 className="size-3" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Delete</TooltipContent>
                                </Tooltip>
                              </div>
                            );
                          })()}
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
                {!isLoading && filteredWorkspaces.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="h-20 px-4 text-center text-gray-500 text-xs dark:text-gray-400">
                      {workspaces.length > 0 ? 'No workspaces' : 'No workspaces yet'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Start Workspace Dialog */}
        {showStartDialog && (
          <div
            className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/40 backdrop-blur-[2px]"
            onClick={handleStartCancel}
          >
            <div
              ref={startDialogRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby="start-dialog-title"
              tabIndex={-1}
              className="mx-4 max-h-[calc(100vh-2rem)] w-full max-w-[400px] overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-xl dark:border-white/10 dark:bg-gray-900"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="border-gray-100 border-b bg-gray-50/50 px-5 py-3 dark:border-white/5 dark:bg-white/5">
                <h3 id="start-dialog-title" className="font-semibold text-base text-gray-900 dark:text-gray-100">
                  New Workspace
                </h3>
              </div>

              {/* Body */}
              <div className="space-y-4 px-5 py-4">
                {/* Workspace Name */}
                <div>
                  <label
                    htmlFor="workspace-name"
                    className="mb-1 block font-medium text-gray-700 text-xs dark:text-gray-300"
                  >
                    Name
                  </label>
                  <input
                    ref={nameInputRef}
                    id="workspace-name"
                    type="text"
                    value={workspaceName}
                    onChange={(e) => {
                      setWorkspaceName(e.target.value);
                      if (nameError) setNameError(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !isStarting) handleStartConfirm();
                    }}
                    placeholder="My Robot Project"
                    disabled={isStarting}
                    className={cn(
                      'h-9 w-full rounded-md border px-3 py-2 text-[13px] transition-colors',
                      'bg-white dark:bg-gray-900',
                      'text-gray-900 dark:text-gray-100',
                      'placeholder:text-gray-400 dark:placeholder:text-gray-500',
                      'focus:outline-none focus:ring-1 focus:ring-offset-0',
                      'disabled:cursor-not-allowed disabled:opacity-50',
                      nameError
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20 dark:border-red-500/50'
                        : 'border-gray-200 focus:border-gray-300 focus:ring-gray-300 dark:border-white/10 dark:focus:border-white/20'
                    )}
                    autoFocus
                  />
                  {nameError && <p className="mt-1.5 text-red-600 text-xs dark:text-red-400">{nameError}</p>}
                </div>

                {/* Image Selector - only when the org has custom images (or their listing failed) */}
                {showImagePicker && (
                  <div>
                    <label className="mb-1 block font-medium text-gray-700 text-xs dark:text-gray-300">Image</label>
                    {hasImageChoice ? (
                      <Select value={selectedImage} onValueChange={handleStartImageChange} disabled={isStarting}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select image" />
                        </SelectTrigger>
                        <SelectContent>
                          {imageNames.map((name) => (
                            <SelectItem key={name} value={name}>
                              <span className="inline-flex items-center gap-2">
                                <span>{imageLabel(name)}</span>
                                {name === defaultImageName && (
                                  <span className="text-[10px] text-gray-400 leading-none">(default)</span>
                                )}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="flex h-9 w-full items-center rounded-md border border-gray-200 bg-gray-50 px-3 dark:border-white/10 dark:bg-gray-900">
                        <span className="text-[13px] text-gray-400">{imageLabel(defaultImageName)}</span>
                      </div>
                    )}
                    {orgImagesError && (
                      <p className="mt-1.5 text-amber-600 text-xs dark:text-amber-400">
                        Custom images are temporarily unavailable; only central images can be selected.
                      </p>
                    )}
                  </div>
                )}

                {/* Version Selector */}
                <div>
                  <label className="mb-1 block font-medium text-gray-700 text-xs dark:text-gray-300">Version</label>
                  {versionsLoading ? (
                    <div className="flex h-9 w-full items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 dark:border-white/10 dark:bg-gray-900">
                      <Spinner size="size-3.5" className="text-gray-400" />
                      <span className="text-[13px] text-gray-400">Loading versions...</span>
                    </div>
                  ) : (
                    <Select value={selectedVersion} onValueChange={setSelectedVersion} disabled={isStarting}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select version" />
                      </SelectTrigger>
                      <SelectContent>
                        {versionsForImage(selectedImage).map((v, i) => (
                          <SelectItem key={v.version} value={v.version}>
                            <span className="inline-flex items-center gap-2">
                              <span>v{v.version}</span>
                              {v.released_at && (
                                <span className="text-[10px] text-gray-400">{formatShortDate(v.released_at)}</span>
                              )}
                              {i === 0 && (
                                <span className="text-[10px] text-green-600 leading-none dark:text-green-400">
                                  (latest)
                                </span>
                              )}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                {/* SSH Keys Info */}
                <div className="flex items-start gap-2 rounded-lg bg-blue-50 px-3 py-2.5 dark:bg-blue-500/10">
                  <Settings className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600 dark:text-blue-400" />
                  <p className="text-blue-700 text-xs dark:text-blue-300">
                    Configure your SSH keys in{' '}
                    <Link
                      to={orgHref('/settings?ssh=add')}
                      className="font-medium underline underline-offset-2 hover:text-blue-800 dark:hover:text-blue-200"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Settings
                    </Link>{' '}
                    to enable SSH access to your workspace.
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-2 border-gray-100 border-t bg-gray-50/50 px-5 py-3 dark:border-white/5 dark:bg-white/5">
                <Button variant="outline" size="sm" onClick={handleStartCancel} disabled={isStarting}>
                  Cancel
                </Button>
                <Button size="sm" onClick={handleStartConfirm} disabled={!workspaceName.trim() || isStarting}>
                  {isStarting ? (
                    <>
                      <Spinner size="h-4 w-4" className="mr-1.5" />
                      Starting...
                    </>
                  ) : (
                    'Create Workspace'
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
        {/* Restart Workspace Dialog */}
        {showRestartDialog && workspaceToRestart && (
          <div
            className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/40 backdrop-blur-[2px]"
            onClick={handleRestartCancel}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="restart-dialog-title"
              tabIndex={-1}
              className="mx-4 max-h-[calc(100vh-2rem)] w-full max-w-[400px] overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-xl dark:border-white/10 dark:bg-gray-900"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="border-gray-100 border-b bg-gray-50/50 px-5 py-3 dark:border-white/5 dark:bg-white/5">
                <h3 id="restart-dialog-title" className="font-semibold text-base text-gray-900 dark:text-gray-100">
                  Restart Workspace
                </h3>
              </div>

              {/* Body */}
              <div className="space-y-4 px-5 py-4">
                <p className="text-[13px] text-gray-600 dark:text-gray-300">
                  Restart{' '}
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {workspaceToRestart.name || workspaceToRestart.id}
                  </span>
                </p>

                {/* Image Selector - only when the org has custom images (or their listing failed) */}
                {showImagePicker && (
                  <div>
                    <label className="mb-1 block font-medium text-gray-700 text-xs dark:text-gray-300">Image</label>
                    {hasImageChoice ? (
                      <Select value={restartImage} onValueChange={handleRestartImageChange}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select image" />
                        </SelectTrigger>
                        <SelectContent>
                          {imageNames.map((name) => (
                            <SelectItem key={name} value={name}>
                              <span className="inline-flex items-center gap-2">
                                <span>{imageLabel(name)}</span>
                                {name === defaultImageName && (
                                  <span className="text-[10px] text-gray-400 leading-none">(default)</span>
                                )}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="flex h-9 w-full items-center rounded-md border border-gray-200 bg-gray-50 px-3 dark:border-white/10 dark:bg-gray-900">
                        <span className="text-[13px] text-gray-400">{imageLabel(defaultImageName)}</span>
                      </div>
                    )}
                    {orgImagesError && (
                      <p className="mt-1.5 text-amber-600 text-xs dark:text-amber-400">
                        Custom images are temporarily unavailable; only central images can be selected.
                      </p>
                    )}
                  </div>
                )}

                {/* Version Selector */}
                <div>
                  <label className="mb-1 block font-medium text-gray-700 text-xs dark:text-gray-300">Version</label>
                  {versionsLoading ? (
                    <div className="flex h-9 w-full items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 dark:border-white/10 dark:bg-gray-900">
                      <Spinner size="size-3.5" className="text-gray-400" />
                      <span className="text-[13px] text-gray-400">Loading versions...</span>
                    </div>
                  ) : (
                    <>
                      <Select value={restartVersion} onValueChange={setRestartVersion}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select version" />
                        </SelectTrigger>
                        <SelectContent>
                          {versionsForImage(restartImage).map((v, i) => (
                            <SelectItem key={v.version} value={v.version}>
                              <span className="inline-flex items-center gap-2">
                                <span>v{v.version}</span>
                                {v.released_at && (
                                  <span className="text-[10px] text-gray-400">{formatShortDate(v.released_at)}</span>
                                )}
                                {i === 0 && (
                                  <span className="text-[10px] text-green-600 leading-none dark:text-green-400">
                                    (latest)
                                  </span>
                                )}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {workspaceToRestart.version && restartVersion !== workspaceToRestart.version && (
                        <p className="mt-1.5 text-amber-600 text-xs dark:text-amber-400">
                          Changing version from v{workspaceToRestart.version} to v{restartVersion}
                        </p>
                      )}
                      {showImagePicker && restartImage !== (workspaceToRestart.image || DEFAULT_IMAGE) && (
                        <p className="mt-1.5 text-amber-600 text-xs dark:text-amber-400">
                          Changing image from {workspaceToRestart.image || DEFAULT_IMAGE} to {restartImage}
                        </p>
                      )}
                    </>
                  )}
                </div>

                {/* SSH Keys Info */}
                <div className="flex items-start gap-2 rounded-lg bg-blue-50 px-3 py-2.5 dark:bg-blue-500/10">
                  <Settings className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600 dark:text-blue-400" />
                  <p className="text-blue-700 text-xs dark:text-blue-300">
                    SSH key changes from{' '}
                    <Link
                      to={orgHref('/settings?ssh=add')}
                      className="font-medium underline underline-offset-2 hover:text-blue-800 dark:hover:text-blue-200"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Settings
                    </Link>{' '}
                    will be applied on restart.
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-2 border-gray-100 border-t bg-gray-50/50 px-5 py-3 dark:border-white/5 dark:bg-white/5">
                <Button variant="outline" size="sm" onClick={handleRestartCancel}>
                  Cancel
                </Button>
                <Button size="sm" onClick={handleRestartConfirm}>
                  Restart
                </Button>
              </div>
            </div>
          </div>
        )}
        {/* Startup History Modal */}
        {progressModalWorkspaceId && (
          <StartupHistoryModal
            workspaceName={workspaces.find((w) => w.id === progressModalWorkspaceId)?.name || progressModalWorkspaceId}
            steps={progressMap[progressModalWorkspaceId] ?? []}
            onClose={() => setProgressModalWorkspaceId(null)}
          />
        )}
      </div>
    </TooltipProvider>
  );
}
